import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Boxes, Camera, Clock, Database, Grid3x3, Loader2, MonitorCheck, MonitorPlay, MonitorX, Pencil, Plus, Settings2, ShieldAlert, Wifi, WifiOff } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { PasekZapelnienia } from '@/components/PasekZapelnienia';
import { ZrzutEkranu } from '@/components/ZrzutEkranu';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import {
  createMachine,
  fetchNextVmid,
  listMachines,
  updateMachine,
  uruchomSpsui,
  type MachineRow,
} from '@/lib/machines-api';
import { UstawieniaWyspy } from '@/pages/UstawieniaWyspy';

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// ─────────────────────────────── formularz ───────────────────────────────────

const FormSchema = z.object({
  // nazwa automatu to VendingMachines.DESCR (32 znaki), kod to VMCODE (50)
  name: z.string().trim().min(1, 'Podaj nazwę').max(32),
  vmCode: z.string().trim().max(50).optional(),
  /**
   * Liczba bebnow. KAZDY beben dostaje wlasny wiersz PICAddressMode, bo klucz tej tabeli
   * to (VMID, PICNUMBER) — bez trybu adresowania beben nie startuje. Dlatego minimum to 1,
   * a nie 0: automat bez bebna nie ma planogramu i jest niezdatny do uzytku.
   */
  drumCount: z.number().int().min(1, 'Automat musi mieć co najmniej jeden bęben').max(64),
  /** 9 = beben natywny/BiPoint, 25 = DynaBox. Wspolrzedne przegrodek licza sie INACZEJ. */
  modeType: z.union([z.literal(9), z.literal(25)]),
});

type FormValues = z.infer<typeof FormSchema>;

function MachineModal({
  open,
  onOpenChange,
  editing,
  groupId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: MachineRow | null;
  groupId: string;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  // kreator pokazuje numer, który dostanie automat — SPS nie ma IDENTITY
  const nextVmid = useQuery({
    queryKey: ['machines', 'next-vmid', groupId],
    queryFn: () => fetchNextVmid(groupId),
    enabled: open && editing === null,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: editing?.name ?? '',
      vmCode: editing?.vmCode ?? '',
      drumCount: editing?.drumCount ?? 1,
      modeType: (editing?.modeTypes?.[0] === 25 ? 25 : 9) as 9 | 25,
    },
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      if (editing) {
        // `drumCount` NIE jest edytowalny: dorzucenie bebna to nowy wiersz PICAddressMode,
        // czyli operacja config-down, a nie metadana read-modelu. Poprzednia wersja pozwalala
        // podbic licznik bez utworzenia bebna — portal pokazywal wtedy automat z bebnem,
        // ktory nie istnieje.
        await updateMachine(editing.id, {
          name: values.name,
          vmCode: values.vmCode?.trim() ? values.vmCode.trim() : null,
        });
        toast.success(t('machines.saved'));
      } else {
        const res = await createMachine({
          groupId,
          name: values.name,
          vmCode: values.vmCode?.trim() || undefined,
          drumCount: values.drumCount,
          modeType: values.modeType,
        });
        toast.success(`${t('machines.created')} (VMID=${res.vmid})`);
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={editing ? t('machines.editTitle') : t('machines.addTitle')}
        description={
          editing
            ? `VMID=${editing.vmid}`
            : nextVmid.data
              ? `${t('machines.willGetVmid')} ${nextVmid.data.nextVmid}`
              : t('common.loading')
        }
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormGrid>
            <Field label={t('machines.name')} htmlFor="name" required error={errors.name?.message}>
              <Input id="name" autoComplete="off" autoFocus {...register('name')} />
            </Field>

            <Field label={t('machines.code')} htmlFor="vmCode" error={errors.vmCode?.message}>
              <Input id="vmCode" autoComplete="off" {...register('vmCode')} />
            </Field>

            {/* Bębny i tryb adresowania ustawia się TYLKO przy tworzeniu: każdy bęben to
                osobny wiersz PICAddressMode, więc dorzucenie go później jest operacją
                config-down, a nie zmianą metadanej. */}
            {!editing && (
              <>
                <Field
                  label={t('machines.drums')}
                  htmlFor="drumCount"
                  required
                  error={errors.drumCount?.message}
                  hint={t('machines.drumsHint')}
                >
                  <Input
                    id="drumCount"
                    type="number"
                    min={1}
                    max={64}
                    {...register('drumCount', { valueAsNumber: true })}
                  />
                </Field>

                <Field
                  label={t('machines.modeType')}
                  htmlFor="modeType"
                  required
                  error={errors.modeType?.message}
                  hint={t('machines.modeTypeHint')}
                >
                  <Select id="modeType" {...register('modeType', { valueAsNumber: true })}>
                    <option value={9}>{t('machines.mode9')}</option>
                    <option value={25}>{t('machines.mode25')}</option>
                  </Select>
                </Field>
              </>
            )}

            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              {editing ? t('machines.editNotice') : t('machines.createNotice')}
            </div>

            <ModalError>{serverError}</ModalError>
          </FormGrid>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────── strona ──────────────────────────────────────

/**
 * Plakietka stanu agenta. Cztery stany, cztery różne komunikaty i kolory — bo wymagają
 * czterech różnych reakcji: nic (`ONLINE`), zgłoszenie do wdrożenia (`NO_AGENT`), dokończenie
 * instalacji (`NEVER_CONNECTED`), diagnostyka łączności (`SILENT`).
 *
 * `SPRZECZNOŚĆ` doklejamy osobno, a nie zamiast: automat raportuje „podłączony", a jego agent
 * milczy — jedna z tych informacji jest nieaktualna i to sam w sobie sygnał.
 */
/**
 * CZY OPROGRAMOWANIE AUTOMATU CHODZI — trzy stany, nie dwa.
 *
 * ⚠️ „NIE SPRAWDZAMY" JEST NEUTRALNE, nie czerwone. Agent starszej wersji i agent na maszynie
 * nie-Windows (warsztat, Smart Sync) nie przysyłają tego nigdy — pokazanie tam „nie działa"
 * kazałoby szukać awarii tam, gdzie nikt niczego nie mierzy.
 *
 * ⚠️ NAZWA PROCESU W DYMKU. „Nie działa" bez odpowiedzi na pytanie „a czego szukałeś" jest
 * bezużyteczne przy kliencie, u którego SPS może nazywać się inaczej.
 */
/** Ile jeszcze pokazujemy ślad po Maincie, który już się zamknął. */
const MAINT_SWIEZY_MIN = 60;

function minutTemu(iso: string | null | undefined): number | null {
  if (iso === null || iso === undefined) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? Math.max(0, Math.round((Date.now() - t) / 60_000)) : null;
}

/**
 * URUCHOMIENIE INTERFEJSU AUTOMATU — akcja przy wierszu, tuż obok plakietki pokazującej jego stan.
 *
 * ⚠️ ISTNIEJE, BO PORTAL POKAZYWAŁ „SPSUI stoi" I NIE UMIAŁ NIC Z TYM ZROBIĆ. Zamknięty interfejs
 * znaczy, że automat nie wydaje, a jedyną drogą naprawy było wejście na maszynę u klienta.
 *
 * ⚠️ OSOBNY KOMPONENT, NIE `onClick` WPISANY W KOLUMNĘ. Definicja kolumn stoi w `useMemo([t, isAdmin])`,
 * więc domknięcie na wyspie i na stanie „trwa" byłoby tam NIEAKTUALNE — objawem byłby przycisk,
 * który po przełączeniu wyspy zleca zadanie na POPRZEDNIEJ. Ten komponent czyta wyspę sam.
 *
 * ⚠️ NIE WYŁĄCZAMY GO, GDY OSTATNI MELDUNEK MÓWI „działa". Stan procesu pochodzi z meldunku, czyli
 * bywa minutę stary, a człowiek klika właśnie wtedy, gdy patrzy na pusty ekran automatu. Podpowiedź
 * mówi, co wiemy; decyzję zostawiamy jemu — a agent drugiego procesu nie uruchomi i tak.
 */
/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PLAKIETKA SPSUI — I JEDNOCZEŚNIE PRZYCISK JEGO URUCHOMIENIA.
 *
 *  ⚠️⚠️ ZGŁOSZONE 15.09.2026: „mamy teraz dwa takie same przyciski — wrzuć funkcjonalność
 *  przycisku od uruchamiania SPS do tego żółtego przycisku, że nie działa". Ikona startu SPSUI
 *  i ikona pulpitu na żywo wyglądały w kolumnie akcji identycznie, a jedna uruchamia interfejs
 *  automatu, druga otwiera zdalny pulpit — dwie różne rzeczy pod tym samym obrazkiem.
 *
 *  ⚠️⚠️ KLIKALNA JEST WYŁĄCZNIE ŻÓŁTA „nie działa" I TYLKO PRZY ŻYWYM AGENCIE. Pozostałe stany tej
 *  kolumny nie są przyciskiem, i to nie z ostrożności:
 *    • „działa" — nie ma czego uruchamiać,
 *    • MAINT (czerwony) — SPS stracił bazę; uruchomienie interfejsu nic tam nie naprawi,
 *      a zasłoniłoby jedyny ekran, z którego widać, co się dzieje,
 *    • „nieznany" — agent milczy, więc nie ma komu zlecić.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  OTWARCIE PULPITU W OSOBNYM OKNIE PRZEGLĄDARKI.
 *
 *  ⚠️⚠️ ZGŁOSZONE 15.09.2026: „przejście na inną stronę panelu je zupełnie wyłącza — raczej to
 *  powinno być w jakimś osobnym okienku". Pływające okno wewnątrz tej strony ginęło razem z jej
 *  trasą. Osobne okno przeżywa nawigację i odświeżenie portalu, daje się położyć na drugim
 *  monitorze, a przesuwa i skaluje je system — czyli mechanizm, którego nie musimy pisać.
 *
 *  ⚠️ NAZWA OKNA ZAWIERA IDENTYFIKATOR MASZYNY, więc drugie kliknięcie w TEN SAM automat wraca do
 *  otwartego okna zamiast zakładać drugą sesję — a druga sesja i tak zostałaby odmówiona (serwer
 *  RFB na maszynie przyjmuje jedno połączenie).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function otworzOknoPulpitu(row: MachineRow, groupId: string | null, gdyZablokowane: () => void): void {
  if (!groupId) return;
  const adres = `/automaty/${row.id}/pulpit?groupId=${encodeURIComponent(groupId)}&nazwa=${encodeURIComponent(row.name)}`;
  const okno = window.open(adres, `pulpit-${row.id}`, 'width=1100,height=760,noopener=no');
  /** ⚠️ Blokada wyskakujących okien jest cicha — bez tego zdania przycisk wygląda na zepsuty. */
  if (okno === null) gdyZablokowane();
  else okno.focus();
}

function SpsUiBadge({ row }: { row: MachineRow }) {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const toast = useToast();
  const [trwa, setTrwa] = useState(false);

  const uruchom = async (): Promise<void> => {
    if (!groupId) return;
    setTrwa(true);
    try {
      const w = await uruchomSpsui(row.id, groupId);
      toast.success(t(w.dolaczone ? 'machines.spsui.dolaczone' : 'machines.spsui.zlecone'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setTrwa(false);
    }
  };
  const stan = row.spsProcessRunning ?? null;
  const nazwa = row.spsProcess ?? 'SPSUI.exe';
  const maint = row.spsMaintRunning ?? null;
  const nazwaMaint = row.spsMaint ?? 'SPSMaint.exe';

  const kiedy =
    row.spsProcessAt === null || row.spsProcessAt === undefined
      ? ''
      : `\n${t('machines.spsui.zmierzono')} ${new Date(row.spsProcessAt).toLocaleString('pl-PL')}`;

  /**
   * ⚠️⚠️ CISZA AGENTA UNIEWAŻNIA KAŻDY ODCZYT SPSUI — I TO STOI PRZED CAŁĄ RESZTĄ, TAKŻE PRZED
   * MAINTEM. Zgłoszone 04.09.2026 ze zrzutu ekranu: wiersz miał czerwone „Nie odpowiada" przy
   * agencie i JEDNOCZEŚNIE zielone „działa" przy SPSUI. Drugie z tych zdań nie może być prawdą,
   * bo pomiar robi agent — ten, który właśnie milczy.
   *
   * ⚠️ TO NIE JEST NIEŚCISŁOŚĆ KOSMETYCZNA. Zielony znaczy „automat wydaje", więc na takim
   * wierszu ktoś przestaje szukać przyczyny stojącego automatu dokładnie tam, gdzie ona jest.
   * Kolumna obok (`BazaWyspyBadge`) tę regułę ma od dawna: zielono TYLKO przy `presence`
   * `ONLINE`. Tu jej brakowało.
   *
   * ⚠️ OSTATNI ODCZYT ZOSTAJE W DYMKU, nie znika. „Nie wiemy" nie ma znaczyć „nie mamy nic" —
   * ostatni znany stan i godzina pomiaru są dalej użyteczne dla kogoś, kto szuka przyczyny.
   */
  const swiezy = row.presence === 'ONLINE';
  if (!swiezy && (stan !== null || maint !== null)) {
    const ostatni =
      maint === true
        ? t('machines.spsui.maint')
        : t(stan === true ? 'machines.spsui.dziala' : 'machines.spsui.stoi');
    return (
      <Badge
        variant="warning"
        title={`${t('machines.spsui.nieznanyHint').replace('{stan}', ostatni)}${kiedy}`}
      >
        <MonitorX className="size-3" />
        {t('machines.spsui.nieznany')}
      </Badge>
    );
  }

  /**
   * ⚠️ MAINT PRZED WSZYSTKIM INNYM, także przed „SPSUI działa". Jego uruchomienie znaczy, że SPS
   * stracił bazę — a wtedy chodzący interfejs nie jest dobrą wiadomością, tylko ekranem, na którym
   * i tak nic się nie wyda. Kolejność tych `if`-ów JEST regułą pierwszeństwa z tego zgłoszenia.
   */
  if (maint === true) {
    return (
      <Badge variant="destructive" title={`${nazwaMaint}${kiedy}\n${t('machines.spsui.maintHint')}`}>
        <ShieldAlert className="size-3" />
        {t('machines.spsui.maint')}
      </Badge>
    );
  }

  if (stan === null) {
    return (
      <span className="text-xs text-[var(--muted-foreground)]" title={t('machines.spsui.brakPomiaru')}>
        —
      </span>
    );
  }

  /**
   * ⚠️ ŚLAD PO MAINCIE, KTÓRY JUŻ SIĘ ZAMKNĄŁ — bez tego cała kolumna łapałaby awarię przez
   * przypadek. Pomiar idzie raz na minutę, a naprawa bazy potrafi przelecieć w kilkanaście sekund,
   * więc sam stan „chodzi teraz" gaśnie, zanim ktokolwiek spojrzy na ekran. Doklejamy, a nie
   * zamieniamy: „SPSUI działa" jest w tej chwili prawdą i nie wolno jej podmieniać na historię.
   */
  const odMaint = minutTemu(row.spsMaintSeenAt);
  const slad =
    odMaint !== null && odMaint <= MAINT_SWIEZY_MIN ? (
      <Badge
        key="slad"
        variant="warning"
        title={`${nazwaMaint}\n${t('machines.spsui.maintHint')}`}
      >
        <ShieldAlert className="size-3" />
        {t('machines.spsui.maintByl').replace('{n}', String(odMaint))}
      </Badge>
    ) : null;

  const glowna = stan ? (
    <Badge variant="success" title={`${nazwa}${kiedy}`}>
      <MonitorCheck className="size-3" />
      {t('machines.spsui.dziala')}
    </Badge>
  ) : (
    /**
     * ⚠️ ŻÓŁTY, NIE CZERWONY — POPRAWKA PO ZGŁOSZENIU. Stało tu „czerwień jest uzasadniona, bo
     * automat stoi", a u klienta SPSUI bywa WYŁĄCZANE CELOWO (serwis, przenosiny, spokój na noc).
     * Czerwień na stan zamierzony uczy patrzącego, że czerwone da się zignorować — i wtedy nie
     * działa wtedy, gdy jest naprawdę potrzebna. Czerwień zostaje dla Mainta, czyli dla stanu,
     * którego nikt nie wywołuje umyślnie.
     */
    /**
     * ⚠️ PRZYCISK, A NIE PLAKIETKA Z `onClick` — żeby dało się tu wejść klawiaturą i żeby czytnik
     * ekranu powiedział, co się stanie. Wygląd zostaje ten sam: to nadal ta sama informacja,
     * tylko teraz da się na nią kliknąć.
     */
    <button
      type="button"
      disabled={trwa}
      onClick={() => void uruchom()}
      title={`${nazwa}${kiedy}\n${t('machines.spsui.stoiHint')}\n${t('machines.spsui.uruchom')}`}
      aria-label={`${t('machines.spsui.uruchom')} ${row.name}`}
      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
    >
      <Badge variant="warning" className="cursor-pointer hover:brightness-95">
        {trwa ? <Loader2 className="size-3 animate-spin" /> : <MonitorPlay className="size-3" />}
        {t('machines.spsui.stoi')}
      </Badge>
    </button>
  );

  if (slad === null) return glowna;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {glowna}
      {slad}
    </span>
  );
}

/**
 * CZY BAZA WYSPY ODPOWIADA — plakietka przy nazwie maszyny, która ją trzyma.
 *
 * ⚠️ ZGŁOSZONE WPROST: portal pokazywał „BAZA WYSPY" i na tym koniec, więc z ekranu nie dało się
 * odczytać rzeczy najważniejszej — czy ta baza żyje. Martwa baza zatrzymuje CAŁĄ wyspę.
 *
 * ⚠️ CZTERY KOLORY, BO SĄ CZTERY STANY. Zielony = ostatni cykl agenta przeczytał bazę. Czerwony =
 * agent jej nie otworzył i wiemy dlaczego (powód w dymku). ŻÓŁTY = mieliśmy odczyt, ale agent
 * milczy, więc nikt tej bazy teraz nie sprawdza. Szary = NIGDY NIE MIERZONO (starszy agent albo
 * maszyna bez bazy). Zielony przy milczącym agencie byłby najgorszym z możliwych: pokazywałby
 * pomiar sprzed godziny jako stan bieżący.
 *
 * ⚠️ ŻÓŁTY I SZARY TO DWA RÓŻNE STANY, NIE ODCIENIE JEDNEGO — do 04.09.2026 dzieliły kolor i to
 * była jedyna rzecz, przez którą ta kolumna mówiła mniej, niż wiedziała.
 */
function BazaWyspyBadge({ row }: { row: MachineRow }) {
  const { t } = useI18n();
  const nazwa = row.databaseName ?? null;
  const podpis = nazwa === null ? t('machines.hasDatabase') : `${t('machines.hasDatabase')} · ${nazwa}`;
  const ok = row.spsDbOk ?? null;
  const kiedy =
    row.spsDbAt === null || row.spsDbAt === undefined
      ? ''
      : `\n${t('machines.spsui.zmierzono')} ${new Date(row.spsDbAt).toLocaleString('pl-PL')}`;
  /** Pomiar starzeje się razem z agentem: jego cisza znaczy, że nikt tej bazy nie sprawdza. */
  const swiezy = row.presence === 'ONLINE';

  /**
   * ⚠️⚠️ CISZA AGENTA ROZSTRZYGA PRZED WSZYSTKIM — dokładnie ta sama reguła i ta sama kolejność
   * co w `SpsUiBadge`. Kolumna miała to już wcześniej dla stanu dobrego (zielono tylko przy
   * `ONLINE`), ale rysowała nieaktualny odczyt NA SZARO, a stan zły — na czerwono niezależnie od
   * milczenia agenta. Wyrównane 04.09.2026 na żądanie: żółto w obu kolumnach znaczy „mieliśmy odczyt
   * i już mu nie wierzymy".
   *
   * ⚠️ CZERWONE „BAZA NIE ODPOWIADA" TEŻ JEST TWIERDZENIEM O TERAZ, i dlatego przy milczącym
   * agencie ustępuje żółtemu. Nic się przy tym nie gubi: ostatni odczyt, jego powód i godzina
   * pomiaru zostają w dymku, a wiersz i tak krzyczy czerwonym „Nie odpowiada" przy samym agencie —
   * czyli przyczyną BARDZIEJ podstawową niż niedostępna baza.
   *
   * ⚠️ ROZRÓŻNIENIE, KTÓRE MUSI ZOSTAĆ: `ok === null` to „NIGDY NIE MIERZONO" (starszy agent albo
   * maszyna bez bazy) i zostaje SZARE. Żółte na to znaczyłoby zamianę zadania wdrożeniowego
   * w usterkę bazy — ten sam błąd, który w kolumnie SPSUI wyklucza „—" z plakietki nieznanego stanu.
   */
  if (!swiezy && ok !== null) {
    return (
      <Badge
        variant="warning"
        title={`${t(ok ? 'machines.bazaNieaktualny' : 'machines.bazaNieaktualnyBlad')}${kiedy}\n\n${
          ok ? '' : (row.spsDbError ?? '')
        }`.trim()}
      >
        <Database className="size-3" />
        {podpis}
      </Badge>
    );
  }

  if (ok === false) {
    return (
      <Badge variant="destructive" title={`${t('machines.bazaNieodpowiada')}${kiedy}\n\n${row.spsDbError ?? ''}`.trim()}>
        <Database className="size-3" />
        {podpis}
      </Badge>
    );
  }

  if (ok === true) {
    return (
      <Badge variant="success" title={`${t('machines.bazaOdpowiada')}${kiedy}\n${t('machines.hasDatabaseHint')}`}>
        <Database className="size-3" />
        {podpis}
      </Badge>
    );
  }

  return (
    <Badge variant="neutral" title={`${t('machines.bazaBrakPomiaru')}\n${t('machines.hasDatabaseHint')}`}>
      <Database className="size-3" />
      {podpis}
    </Badge>
  );
}

function PresenceBadge({ row }: { row: MachineRow }) {
  const { t } = useI18n();
  const label = t(`machines.presence.${row.presence}`);

  const badge =
    row.presence === 'ONLINE' ? (
      <Badge variant="success">
        <Wifi className="size-3" />
        {label}
      </Badge>
    ) : row.presence === 'SILENT' ? (
      <Badge variant="destructive">
        <WifiOff className="size-3" />
        {label}
      </Badge>
    ) : row.presence === 'NEVER_CONNECTED' ? (
      <Badge variant="warning">
        <WifiOff className="size-3" />
        {label}
      </Badge>
    ) : (
      // brak agenta = NEUTRALNIE. Czerwień sugerowałaby awarię tam, gdzie nic nigdy nie działało.
      <Badge variant="neutral">
        <WifiOff className="size-3" />
        {label}
      </Badge>
    );

  /**
   * ⚠️ „CZEKA NA POTWIERDZENIE" DOKLEJAMY, A NIE ZAMIENIAMY — jak sprzeczność.
   *
   * Taki agent MELDUJE SIĘ prawidłowo, więc `ONLINE` jest prawdą i nie wolno go podmienić na coś
   * innego. Ale portal nie przyjmuje od niego ani konfiguracji, ani wydań, więc sama zielona
   * plakietka byłaby prawdą wprowadzającą w błąd: wszystko wygląda dobrze, a dane nie płyną.
   */
  const dodatki = [
    row.contradiction ? (
      <Badge key="sprzecznosc" variant="warning" title={t('machines.presence.contradictionHint')}>
        {t('machines.presence.contradiction')}
      </Badge>
    ) : null,
    row.agentAwaitingAcceptance ? (
      <Badge key="potwierdzenie" variant="warning" title={t('machines.presence.awaitingHint')}>
        {t('machines.presence.awaiting')}
      </Badge>
    ) : null,
  ].filter((x) => x !== null);

  if (dodatki.length === 0) return badge;
  return (
    <span className="inline-flex flex-wrap items-center justify-center gap-1">
      {badge}
      {dodatki}
    </span>
  );
}

export function Machines() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { groupId, island } = useIslands();

  /**
   * ⚠️ ZAKŁADKA W ADRESIE (`?zakladka=ustawienia`), jak na Smart Syncu — link „Cała wyspa" z karty
   * automatu i odświeżenie strony trafiają w to samo miejsce. Przeniesione z menu 23.09.2026:
   * „dodaj jako zakładkę w automatach, nie na głównej liście".
   *
   * ⚠️ USTAWIENIA TYLKO DLA ADMINA: reszta widzi samą listę, bez paska (jedna zakładka to pasek
   * bez wyboru), a adres z `?zakladka=ustawienia` daje jej listę.
   */
  const [parametry, ustawParametry] = useSearchParams();
  const zakladka: 'lista' | 'ustawienia' = isAdmin && parametry.get('zakladka') === 'ustawienia' ? 'ustawienia' : 'lista';

  const [state, setState] = useState<DataTableState>({
    search: '',
    page: 1,
    pageSize: 25,
    sort: 'vmid',
    dir: 'asc',
  });
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MachineRow | null>(null);
  /** Automat, którego pulpit właśnie oglądamy. `null` = okienko zamknięte. */
  const [zrzutMaszyna, setZrzutMaszyna] = useState<{ id: string; name: string } | null>(null);

  const params = { ...state, onlyOnline, groupId: groupId ?? '' };

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['machines', params],
    queryFn: () => listMachines(params),
    enabled: Boolean(groupId) && zakladka === 'lista',
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  const columns = useMemo<ColumnDef<MachineRow>[]>(
    () => [
      {
        id: 'vmid',
        header: 'VMID',
        meta: { exportValue: (r) => r.vmid, align: 'right' },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.vmid}</span>,
      },
      {
        id: 'name',
        header: t('machines.name'),
        meta: {
          exportValue: (r) => (r.hasDatabase ? `${r.name} [${t('machines.hasDatabase')}]` : r.name),
        },
        cell: ({ row }) => (
          /**
           * ⚠️ `w-full` + `flex-1` NA NAZWIE, żeby znacznik bazy trzymał się PRAWEJ krawędzi
           * kolumny, a nie doklejał się do nazwy. Zgłoszone (26.08.2026): przy różnej długości
           * nazw automatów znaczniki stały w różnych miejscach i wiersze czytały się jak
           * poszarpane, choć niosą tę samą informację.
           */
          <span className="flex w-full items-center gap-2">
            {/**
             * ⚠️ NAZWA JEST WEJŚCIEM DO KARTY AUTOMATU — tak samo jak nazwisko na liście
             * pracowników. Lista odpowiada na „czy wszystkie żyją", karta na „co się dzieje
             * z TĄ maszyną", a to drugie pytanie zadaje się zawsze o konkretny wiersz.
             */}
            <span className="min-w-0 flex-1">
              <Link to={`/automaty/${row.original.id}`} className="block truncate font-medium hover:underline">
                {row.original.name}
              </Link>
              {/**
                * ⚠️ NAZWA KOMPUTERA POD NAZWĄ AUTOMATU — tak samo jak na liście agentów.
                *
                * ⚠️⚠️ ZGŁOSZONE PRZY PRACY NA WDROŻENIU (26.08.2026): „teraz nie wiadomo, która
                * maszyna jest która". `PESA-1`, `PESA-2`, `PESA-3` to nazwy z SPS-a, których
                * technik stojący przy sprzęcie nie ma jak przypisać do maszyn — a nazwa komputera
                * jest tym, co widzi na ekranie automatu i w zdalnym pulpicie.
                */}
              {row.original.agent?.hostname != null && row.original.agent.hostname !== '' && (
                <span className="block truncate font-mono text-[11px] text-muted-foreground">
                  {row.original.agent.hostname}
                </span>
              )}
            </span>
            {/**
             * ⚠️ ZNACZNIK BAZY STOI PRZY NAZWIE, nie w osobnej kolumnie na końcu wiersza —
             * bo to jest informacja, którą trzeba zobaczyć ZANIM się cokolwiek z tą maszyną
             * zrobi. Wyłączenie albo przeniesienie tego automatu zatrzymuje synchronizację
             * CAŁEJ wyspy, a nie tylko jego samego.
             */}
            {row.original.hasDatabase === true && <BazaWyspyBadge row={row.original} />}
            {row.original.pending && (
              <Badge variant="warning" title={t('machines.pendingHint')}>
                <Clock className="size-3" />
                {t('machines.pending')}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'vmCode',
        header: t('machines.code'),
        meta: { exportValue: (r) => r.vmCode ?? '' },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.vmCode ?? '—'}</span>
        ),
      },
      {
        id: 'drumCount',
        header: t('machines.drums'),
        meta: { exportValue: (r) => r.drumCount, align: 'right' },
        cell: ({ row }) => row.original.drumCount,
      },
      {
        id: 'fill',
        header: t('machines.fill'),
        enableSorting: false,
        meta: {
          exportValue: (r) => (r.fillPercent === null ? '' : `${r.fillPercent}%`),
        },
        cell: ({ row }) => {
          const f = row.original.fillPercent;
          if (f === null) return <span className="text-xs text-muted-foreground">{t('machines.noPlanogram')}</span>;
          /**
           * ⚠️ WSPÓLNY `PasekZapelnienia`, NIE WŁASNY PASEK. Stał tu drugi, narysowany od nowa —
           * z szarym torem bez obwódki i z INNYMI progami (25/50 zamiast 34/67). Skutek widziało
           * się na ekranie: automat z zerowym zapełnieniem wyglądał identycznie jak automat bez
           * planogramu, bo przy 0 % nie ma czego pokolorować, a szary tor nic nie mówi. Zgłoszone
           * słowami „dodaj też te kolory obramowania, tak samo jak w artykułach".
           *
           * Komentarz w samym komponencie ostrzegał dokładnie przed tym: „drugie miejsce rysujące
           * to samo od nowa prędzej czy później rozjedzie się z pierwszym, a próg »ile procent to
           * już czerwony« musi znaczyć na obu ekranach dokładnie to samo". Rozjechało się.
           */
          return (
            <div className="flex items-center gap-2">
              <PasekZapelnienia percent={f} szeroki />
              <span className="text-xs tabular-nums text-muted-foreground">{f}%</span>
            </div>
          );
        },
      },
      {
        id: 'compartments',
        header: t('machines.compartmentsFilled'),
        enableSorting: false,
        meta: {
          exportValue: (r) =>
            r.compartmentsTotal === undefined ? r.compartments : `${r.compartments}/${r.compartmentsTotal}`,
          align: 'right',
        },
        /**
         * ⚠️ STAN I MIANOWNIK, NIE SAMA LICZBA. Zgłoszone (26.08.2026): kolumna pokazywała samo
         * „611" i nie dawało się odczytać, czy to dużo. Licznik to przegródki Z PRZYPISANYM
         * TOWAREM, mianownik to wszystkie przegródki automatu — dopiero razem odpowiadają na
         * pytanie „ile miejsca w planogramie jest jeszcze nieużyte".
         *
         * ⚠️ NAZWA KOLUMNY ZOSTAJE, a wyjaśnienie idzie w dymek — tak poprosił użytkownik i tak
         * jest lepiej: nagłówek ma się mieścić w wąskiej kolumnie.
         */
        cell: ({ row }) => (
          <span className="tabular-nums" title={t('machines.compartmentsHint')}>
            {row.original.compartments}
            {row.original.compartmentsTotal !== undefined && (
              <span className="text-muted-foreground">/{row.original.compartmentsTotal}</span>
            )}
          </span>
        ),
      },
      {
        id: 'online',
        header: t('machines.status'),
        enableSorting: false,
        meta: { exportValue: (r) => t(`machines.presence.${r.presence}`), align: 'center' },
        /**
         * ⚠️ TA KOLUMNA POKAZYWAŁA `online`/`offline` z jednego boola i przez to KŁAMAŁA:
         * maszyna, na której nikt nie zainstalował agenta, dostawała „offline", jakby
         * zerwała się łączność. Teraz pokazuje WYLICZONY stan agenta — a „brak agenta"
         * jest neutralny, bo to zadanie wdrożeniowe, nie awaria.
         */
        cell: ({ row }) => <PresenceBadge row={row.original} />,
      },
      {
        id: 'spsui',
        header: t('machines.spsui'),
        enableSorting: false,
        meta: {
          /**
           * ⚠️ TA SAMA KOLEJNOŚĆ CO W PLAKIETCE — Maint przed wszystkim, a cisza agenta przed
           * Maintem. Eksport mówiący „działa" o wierszu, na którym ekran pokazuje „stan nieznany",
           * byłby gorszy od braku eksportu: rozjazd między tym, co widać, a tym, co się wysyła.
           */
          exportValue: (r) => {
            const brakPomiaru = r.spsProcessRunning === null || r.spsProcessRunning === undefined;
            if (r.presence !== 'ONLINE' && !(brakPomiaru && r.spsMaintRunning !== true)) {
              return t('machines.spsui.nieznany');
            }
            if (r.spsMaintRunning === true) return t('machines.spsui.maint');
            return brakPomiaru ? '—' : t(r.spsProcessRunning ? 'machines.spsui.dziala' : 'machines.spsui.stoi');
          },
          align: 'center',
        },
        /**
         * ⚠️ OSOBNA KOLUMNA OD STANU AGENTA, i to jest jej cały sens. „Agent online" znaczy, że
         * NASZA usługa się melduje — a ona melduje się dokładnie tak samo na maszynie, na której
         * oprogramowanie automatu padło godzinę temu. Taka maszyna wyglądała dotąd na zdrową,
         * podczas gdy pracownik stał przed automatem bez reakcji.
         */
        cell: ({ row }) => <SpsUiBadge row={row.original} />,
      },
      {
        id: 'lastSeenAt',
        header: t('machines.lastSeen'),
        /**
         * ⚠️ Czyta kontakt AGENTA, nie `lastSeenAt` automatu. Wcześniej pokazywało samo-raport
         * maszyny i po dodaniu stanów agenta dwie sąsiednie kolumny mówiły sprzeczne rzeczy:
         * „agent nigdy się nie połączył” obok konkretnej godziny kontaktu.
         */
        meta: { exportValue: (r) => fmtDate(r.agentLastSeenAt) },
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{fmtDate(row.original.agentLastSeenAt)}</span>
        ),
      },
      {
        /**
         * WERSJA AGENTA ZAINSTALOWANA NA TYM AUTOMACIE.
         *
         * ⚠️ Zgłoszone wprost: „wersja agenta jest tylko w miejscu do pobrania, ale nie widzę
         * aktualnie zainstalowanej na automacie". Portal pokazywał wersję DOCELOWĄ (tę z instalatora)
         * i to jest inna informacja niż „co realnie chodzi na tej maszynie" — a przy agencie, który
         * aktualizuje się sam i tylko wtedy, gdy zapyta, rozjazd między nimi jest normalnym stanem
         * przez kilkadziesiąt minut. Dane były w odpowiedzi API od dawna, brakowało kolumny.
         */
        id: 'agentVersion',
        header: t('machines.agentVersion'),
        enableSorting: false,
        meta: { exportValue: (r) => r.agent?.version ?? '' },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.agent?.version ?? '—'}</span>
        ),
      },
      ...(isAdmin
        ? [
            {
              id: 'actions',
              header: t('table.actions'),
              enableSorting: false,
              meta: { align: 'right' as const },
              cell: ({ row }) => (
                <div className="flex justify-end">
                  {/*
                    ⚠️ WYŁĄCZONY, GDY AGENT NIE JEST `ONLINE` — a nie ukryty. Ukrycie ikony kazałoby
                    się domyślać, czego brakuje; wyłączony przycisk z podpowiedzią mówi wprost,
                    dlaczego zrzut nie ma skąd przyjść. To ta sama informacja, którą oddaje API,
                    tylko pokazana przed kliknięciem.
                  */}
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={row.original.presence !== 'ONLINE'}
                    title={row.original.presence === 'ONLINE' ? t('screenshot.action') : t('screenshot.offline')}
                    aria-label={`${t('screenshot.action')} ${row.original.name}`}
                    onClick={() => setZrzutMaszyna({ id: row.original.id, name: row.original.name })}
                  >
                    <Camera />
                  </Button>
                  {/*
                    ⚠️⚠️ PULPIT NA ŻYWO OBOK ZRZUTU, I TO SĄ DWIE RÓŻNE RZECZY. Zrzut odpowiada na
                    „co tam teraz jest" i kosztuje jedno zdjęcie; pulpit pozwala KLIKAĆ i trzyma
                    otwarty serwer w sesji użytkownika przy automacie. Dlatego ta sama zasada co
                    przy zrzucie — wyłączony, gdy agent milczy — i osobna ikona, żeby nikt nie
                    otworzył sesji, chcąc zrobić zdjęcie.
                  */}
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={row.original.presence !== 'ONLINE'}
                    title={row.original.presence === 'ONLINE' ? t('pulpit.action') : t('pulpit.offline')}
                    aria-label={`${t('pulpit.action')} ${row.original.name}`}
                    onClick={() =>
                      otworzOknoPulpitu(row.original, groupId, () => toast.error(t('pulpit.zablokowane')))
                    }
                  >
                    <MonitorPlay />
                  </Button>
                  <Button asChild variant="ghost" size="icon" title={t('nav.planogram')}>
                    <Link to={`/planogram?machine=${row.original.id}`} aria-label={`${t('nav.planogram')} ${row.original.name}`}>
                      <Grid3x3 />
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title={t('common.edit')}
                    aria-label={`${t('common.edit')} ${row.original.name}`}
                    onClick={() => {
                      setEditing(row.original);
                      setModalOpen(true);
                    }}
                  >
                    <Pencil />
                  </Button>
                </div>
              ),
            } satisfies ColumnDef<MachineRow>,
          ]
        : []),
    ],
    [t, isAdmin],
  );

  const fetchAllRows = useCallback(async (): Promise<MachineRow[]> => {
    if (!groupId) return [];
    const out: MachineRow[] = [];
    for (let page = 1; ; page += 1) {
      const chunk = await listMachines({ ...params, page, pageSize: 200 });
      out.push(...chunk.rows);
      if (out.length >= chunk.total || chunk.rows.length === 0) break;
    }
    return out;
  }, [params, groupId]);

  if (!groupId) {
    return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;
  }

  const zakladki = isAdmin ? (
    <Tabs
      zakladki={[
        { id: 'lista', etykieta: t('machines.zakladka.lista'), ikona: <Boxes className="size-4" /> },
        { id: 'ustawienia', etykieta: t('machines.zakladka.ustawienia'), ikona: <Settings2 className="size-4" /> },
      ]}
      wybrana={zakladka}
      onWybor={(z) => ustawParametry(z === 'lista' ? {} : { zakladka: z }, { replace: true })}
    />
  ) : null;

  return (
    <>
      {zakladka === 'ustawienia' ? (
        <div className="space-y-4">
          {/* ⚠️ Wysokość wiersza tytułu jak w tabeli (z przyciskiem), żeby pasek zakładek nie skakał. */}
          <div className="flex min-h-9 items-end">
            <h1 className="text-2xl font-semibold tracking-tight">{t('nav.machines')}</h1>
          </div>
          {zakladki}
          <UstawieniaWyspy />
        </div>
      ) : (
        <DataTable<MachineRow>
          title={t('nav.machines')}
          podTytulem={zakladki}
          columns={columns}
          data={data?.rows ?? []}
          total={data?.total ?? 0}
          state={state}
          onStateChange={onStateChange}
          isLoading={isPending}
          isFetching={isFetching}
          onRefresh={() => void refetch()}
          rowKey={(r) => r.id}
          exportFileName={`automaty-${island?.code ?? 'wyspa'}`}
          fetchAllRows={fetchAllRows}
          reportKey="machines"
          reportGroupId={groupId}
          actions={
            isAdmin ? (
              <Button
                onClick={() => {
                  setEditing(null);
                  setModalOpen(true);
                }}
              >
                <Plus />
                {t('machines.add')}
              </Button>
            ) : null
          }
          /** Jeden ptaszek, więc i pasek ma jedną pozycję — ale to ta, która przycina listę. */
          aktywneFiltry={onlyOnline ? [t('machines.onlyOnline')] : []}
          onWyczyscFiltry={() => {
            setOnlyOnline(false);
            onStateChange({ page: 1 });
          }}
          filters={
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-3.5 accent-[var(--primary)]"
                checked={onlyOnline}
                onChange={(e) => {
                  setOnlyOnline(e.target.checked);
                  onStateChange({ page: 1 });
                }}
              />
              {t('machines.onlyOnline')}
            </label>
          }
        />
      )}

      {modalOpen && (
        <MachineModal
          key={editing?.id ?? 'new'}
          open={modalOpen}
          onOpenChange={setModalOpen}
          editing={editing}
          groupId={groupId}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['machines'] })}
        />
      )}


      {/*
        ⚠️ MONTOWANE TYLKO PRZY OTWARTYM OKIENKU (`zrzutMaszyna &&`), i to nie jest oszczędność
        renderowania: komponent ZLECA zrzut w `useEffect` przy pojawieniu się maszyny, więc
        trzymanie go stale w drzewie znaczyłoby zlecenie przy każdym wejściu na ekran automatów.
      */}
      {zrzutMaszyna && groupId && (
        <ZrzutEkranu maszyna={zrzutMaszyna} groupId={groupId} onClose={() => setZrzutMaszyna(null)} />
      )}
    </>
  );
}
